import { Divider, Headline, Text, TextButton, TextLink } from '@oneaudi/unified-web-components';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { mapTrackingData } from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/models/map-tracking-data';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import placeholder from '~/assets/icons/placeholder.svg';
import { useFootnotes } from '~/components/footnotes';
import { PRICE_DISCLAIMER_REMARK, STOCK_CAR_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { PriceFragment, StockCarNewFragment, StockCarUsedFragment } from '~/graphql/__generated__/graphql';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { StyledResultTile } from '../styles/styled-result-tile';

export default function ResultTile({
	similarCar,
	carIndex,
}: {
	similarCar: StockCarNewFragment | StockCarUsedFragment;
	carIndex: number;
}) {
	const [isFeaturesVisible, setIsFeaturesVisible] = useState(false);
	const [tileTopHeight, setTileTopHeight] = useState<number | undefined>(undefined);
	const { formatCurrency } = useCurrencyFormatter();
	const { stockCar, score } = similarCar;
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const { getReference } = useFootnotes();

	const tileTopRef = useRef<HTMLDivElement>(null);

	const formattedScore = useMemo(() => (score ? `${Math.floor(score * 100)}%` : ''), [score]);

	const { url } = !!stockCar?.images && stockCar?.images[0] ? stockCar.images[0] : { url: placeholder };

	const priceFragment: PriceFragment | undefined =
		stockCar && stockCar.carPrices && stockCar.carPrices.length > 0
			? stockCar.carPrices.length > 1 && stockCar.carPrices[0].price.value < stockCar.carPrices[1].price.value
				? stockCar.carPrices[1].price
				: stockCar.carPrices[0].price
			: undefined;

	const missingFeatures = similarCar.compareCriteria?.filter((_, index) => index < 4);
	const externalUrl = stockCar ? config.stockCarConfig.url.replace('{:vehicleId}', stockCar.id) : '';

	const handleImageLoad = useCallback(() => {
		// Set the height of the top part of the tile to keep the card's height when clicking on differing features
		if (tileTopRef.current) {
			setTileTopHeight(tileTopRef.current.clientHeight);
		}
	}, []);

	const trackClickEvent = useCallback(
		(eventAction: EventAction, eventName: string, label: string, elementName: string, targetURL?: string) => {
			appTrackingService.stockCarClickEvent({
				eventAction,
				eventName,
				label,
				targetURL,
				elementName,
				value: formattedScore,
				pos: `${carIndex + 1}`,
				relatedProducts: mapTrackingData(similarCar, priceFragment?.value),
			});
		},
		[],
	);

	const handleFeaturesClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();
			setIsFeaturesVisible(!isFeaturesVisible);
			!isFeaturesVisible &&
				trackClickEvent(
					EventAction.Content,
					"configurator (stock car matching) - click to open 'differing features'",
					i18n('ngc.stockcar.matches.differing'),
					'text link',
				);
		},
		[isFeaturesVisible],
	);

	return (
		<StyledResultTile
			$height={tileTopHeight}
			$isFeaturesVisible={isFeaturesVisible}
			target="_blank"
			rel="noopener noreferrer"
			onClick={() =>
				trackClickEvent(
					EventAction.ExitLink,
					'configurator (stock car matching) - click on vtp link',
					i18n('ngc.stockcar.matches.details'),
					'button',
					externalUrl,
				)
			}
			href={externalUrl}>
			<div ref={tileTopRef} className="tile--top">
				<div className="tile--top--header">
					<div className="tile--match-container">
						<Headline tag="h1" variant={{ type: 'headline', order: '4', weight: 'bold' }}>
							{formattedScore}
						</Headline>
						<Text tag="p" variant={{ order: '1', style: 'bold', type: 'copy' }}>
							{i18n('ngc.stockcar.matches.match')}
							{<FootnoteReferenceNgc reference={getReference(STOCK_CAR_REMARK)} />}
						</Text>
					</div>

					<TextLink
						disabled={!missingFeatures?.length}
						onClick={(event) => {
							handleFeaturesClick(event);
						}}
						size="md"
						variant={'standalone'}
						icon={'forward'}
						iconPosition={'trailing'}>
						{isFeaturesVisible ? i18n('ngc.actions.close') : i18n('ngc.stockcar.matches.differing')}
					</TextLink>
				</div>
				{isFeaturesVisible && (
					<ol>
						{missingFeatures?.map(({ text }, index: number) => (
							<Text tag="p" key={index} variant={{ order: '3', style: 'normal', type: 'copy' }}>
								<li dangerouslySetInnerHTML={{ __html: text }} />
							</Text>
						))}
					</ol>
				)}
				{!isFeaturesVisible && (
					<img
						onLoad={handleImageLoad}
						alt={stockCar?.model?.name ?? 'Stock Car placeholder image'}
						src={url}
					/>
				)}
			</div>
			<div className="tile--bottom">
				<Headline className="car-name" tag="h2" variant={{ type: 'headline', order: '4', weight: 'bold' }}>
					{stockCar?.model?.name}{' '}
				</Headline>
				{priceFragment && (
					<Text className="price" tag="p" variant={{ order: '1', style: 'bold', type: 'copy' }}>
						{formatCurrency(priceFragment)}
						{config.stockCarConfig.pricedisclaimer ? (
							<FootnoteReferenceNgc reference={getReference(PRICE_DISCLAIMER_REMARK)} />
						) : (
							<></>
						)}
					</Text>
				)}
				<Divider className="divider" />
				<Text tag="p" variant={{ order: '2', style: 'normal', type: 'copy' }}>
					{stockCar?.dealer.name}
				</Text>
				<Text className="city" tag="p" variant={{ order: '3', style: 'normal', type: 'copy' }}>
					{stockCar?.dealer.city}
				</Text>

				<TextButton
					className="cta"
					isFullWidth
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					onClick={() => {}}
					size="sm"
					variant="secondary">
					{i18n('ngc.stockcar.matches.details')}
				</TextButton>
			</div>
		</StyledResultTile>
	);
}
